import { useEffect, useMemo, useRef } from 'react';
import { Outlet, useLocation, useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import type { AxiosError } from 'axios';

import { authUtils } from '@/services/auth';
import { companyAPI } from '@/services/company';
import { toast } from 'sonner';

export default function CompanyGuard() {
  const navigate = useNavigate();
  const location = useLocation();
  const isAuthed = authUtils.isAuthenticated();
  const authData = authUtils.getAuthData();
  const shouldCheckCompany = !authData?.company; // only check when no company in storage

  const {
    data: companies = [],
    isLoading,
    isError,
    error,
    refetch,
  } = useQuery({
    queryKey: ['companies-guard'],
    queryFn: companyAPI.getAllCompanies,
    enabled: shouldCheckCompany,
    retry: false,
    staleTime: 60_000,
  });

  const errorMessage = useMemo(() => {
    if (!isError) return null;
    const err = error as Error | null;
    return err?.message || 'Failed to verify company information.';
  }, [isError, error]);

  const isNetworkError = useMemo(() => {
    if (!isError) return false;
    const axiosErr = error as AxiosError | Error | undefined;
    if (!axiosErr) return false;
    if (
      'code' in axiosErr &&
      typeof axiosErr.code === 'string' &&
      axiosErr.code === 'ERR_NETWORK'
    ) {
      return true;
    }
    if (axiosErr.message?.toLowerCase().includes('network')) {
      return true;
    }
    if ('response' in axiosErr && axiosErr.response === undefined) {
      return true;
    }
    return false;
  }, [isError, error]);

  const is401Error = useMemo(() => {
    if (!isError) return false;
    const axiosErr = error as AxiosError | Error | undefined;
    if (!axiosErr) return false;

    // Check if it's a 401 error from axios response
    if ('response' in axiosErr && axiosErr.response?.status === 401) {
      return true;
    }

    // Check error message
    if (
      axiosErr.message?.includes('401') ||
      axiosErr.message === 'Request failed with status 401'
    ) {
      return true;
    }

    return false;
  }, [isError, error]);

  // Check if user has a company (handle both array and object responses)
  const hasCompany = useMemo(() => {
    if (!companies) return false;
    // Check if companies is an array with at least one item
    if (Array.isArray(companies)) {
      return companies.length > 0;
    }
    // If it's an object, check if it has any properties (not empty object)
    if (typeof companies === 'object' && companies !== null) {
      const keys = Object.keys(companies);
      return keys.length > 0;
    }
    return false;
  }, [companies]);

  const wasNetworkErrorRef = useRef(false);

  // Handle 401 errors - redirect to sign-in (works for all queries, not just company check)
  useEffect(() => {
    if (!is401Error) return;

    // Don't redirect if already on sign-in or sign-up page
    if (
      location.pathname.includes('/sign-in') ||
      location.pathname.includes('/sign-up')
    ) {
      return;
    }

    // Clear auth data
    authUtils.clearAuthData();

    // Show toast and redirect
    toast.error('Session expired', {
      description: 'Your session has expired. Please sign in again.',
    });

    // Redirect to sign-in
    navigate('/sign-in', { replace: true });
  }, [is401Error, navigate, location.pathname]);

  useEffect(() => {
    if (!shouldCheckCompany) return;
    if (!isError || !errorMessage) return;
    // Don't show notifications for 401 errors (we redirect instead)
    if (is401Error) return;

    const notify = () => {
      toast.error(
        isNetworkError ? 'No internet connection' : 'Unable to verify company',
        {
          description: isNetworkError
            ? 'Please check your internet connection and try again.'
            : errorMessage,
        }
      );
    };

    notify();
    const intervalId = window.setInterval(notify, 3000);

    return () => {
      window.clearInterval(intervalId);
    };
  }, [isError, errorMessage, shouldCheckCompany, isNetworkError, is401Error]);

  useEffect(() => {
    if (!shouldCheckCompany) return;
    if (!isNetworkError) return;

    const handleOnline = () => {
      toast.success('Connection restored', {
        description: 'Trying again to verify your company information.',
      });
      refetch();
    };

    window.addEventListener('online', handleOnline);
    return () => {
      window.removeEventListener('online', handleOnline);
    };
  }, [isNetworkError, refetch, shouldCheckCompany]);

  useEffect(() => {
    if (!shouldCheckCompany) return;
    const wasNetworkError = wasNetworkErrorRef.current;
    if (wasNetworkError && !isNetworkError) {
      toast.success('Connection restored', {
        description: 'Resuming verification checks.',
      });
      refetch();
    }
    wasNetworkErrorRef.current = isNetworkError;
  }, [isNetworkError, refetch, shouldCheckCompany]);

  useEffect(() => {
    if (!isAuthed) return;
    if (!shouldCheckCompany) return;
    const isOnSignup = location.pathname.startsWith('/sign-up');
    if (isOnSignup) return;

    // Only redirect to step 2 when: user has no company locally AND companies list is empty or empty object
    if (!isLoading && !isError && !isNetworkError && !hasCompany) {
      navigate('/sign-up?step=2', { replace: true });
    }
  }, [
    isAuthed,
    shouldCheckCompany,
    isError,
    isLoading,
    location.pathname,
    navigate,
    hasCompany,
    isNetworkError,
  ]);

  return <Outlet />;
}
